import { useState } from 'react';
import { useDeepEffect } from '@hooks/useDeepEffect';

import {
  ProductDetailsFragment,
  ProductVariantDetailsFragment,
} from '@sdk/gqlTypes/generatedOperations';

type ProductVariantsPricing = ProductVariantDetailsFragment['pricing'];

type SelectedVariant = {
  variantId: string | null;
  variantStock: number;
  variantPricing: ProductVariantsPricing | null;
};

/**
 * Adjusts the pricing of a variant, maintaining any existing discounts.
 *
 * Takes a ProductVariantsPricing object and a price formula string
 * with variables, and returns a new ProductVariantsPricing object
 * with the price adjusted based on the formula evaluation.
 *
 * Calculates the current discount amount and applies it to the new price
 * to maintain the same effective discount.
 *
 * Also updates the onSale flag based on whether a discount exists or not.
 */
const adjustPrice = (
  pricing: ProductVariantsPricing,
  adjustedPrice: number
): ProductVariantsPricing => {
  // Calculate the difference between the current price and the undiscounted price
  const discountDifferenceGross =
    pricing.priceUndiscounted.gross.amount - pricing.price.gross.amount;
  const discountDifferenceNet =
    pricing.priceUndiscounted.net.amount - pricing.price.net.amount;

  // Apply the adjusted price, maintaining the discount difference
  pricing.price.gross.amount = adjustedPrice;
  pricing.price.net.amount = adjustedPrice;

  // Adjust the undiscounted price to maintain the same difference
  pricing.priceUndiscounted.gross.amount =
    adjustedPrice + discountDifferenceGross;
  pricing.priceUndiscounted.net.amount = adjustedPrice + discountDifferenceNet;

  // Update the onSale flag based on whether there is a discount or not
  pricing.onSale =
    pricing.price.gross.amount !== pricing.priceUndiscounted.gross.amount;

  return pricing;
};

/**
 * Evaluates a pricing formula based on product attributes.
 *
 * Takes a Record of attribute key/values and returns the calculated price.
 * Applies various price adjustments based on specific attribute values.
 */
const priceFormulaEvalFunction = (attributes: Record<string, any>): number => {
  let { price } = attributes;

  if (attributes['glass-type'] === 'double-mirror') {
    price += 165;
  }

  if (attributes['door-style'] === 'verriere') {
    price += 245;
  }

  if (parseInt(attributes.height || '2100', 10) % 100 !== 0) {
    price += 100;
  }

  if (parseInt(attributes.height || '2100', 10) > 2300) {
    price += 100;
  }

  return price;
};

/**
 * Hook to determine the currently selected variant ID, stock level, and pricing
 * for a product based on selected attribute values.
 *
 * Tracks the selected variant and pricing as the attribute values change.
 */
export const useVariantId = (
  attributesFormData: Record<string, string>,
  product: ProductDetailsFragment
): SelectedVariant => {
  const [variantId, setVariantId] = useState(null);
  const [variantStock, setVariantStock] = useState<number>(0);
  const [variantPricing, setVariantPricing] =
    useState<ProductVariantsPricing | null>(null);
  // const dependency = Object.values(attributesFormData)
  // .filter((s) => s)
  //  .join('');

  useDeepEffect(() => {
    const updateVariant = () => {
      const selectedVariant = product.variants.find((productVariant) =>
        productVariant.attributes.every((productVariantAttribute) => {
          const productVariantSlug = productVariantAttribute.values[0]?.slug;
          const attributesFormSlug =
            attributesFormData[productVariantAttribute.attribute.slug];

          const isVariantAttributeSelected =
            productVariantSlug === attributesFormSlug;
          return isVariantAttributeSelected;
        })
      );
      let adjustedPricing = structuredClone(selectedVariant?.pricing);
      const {productType} = product;

      if (productType.isPriceFormula && productType.priceFormula) {
        const adjustedPrice = priceFormulaEvalFunction({
          ...attributesFormData,
          price: adjustedPricing.price.gross.amount,
        });
        if (adjustedPrice) {
          adjustedPricing = adjustPrice(adjustedPricing, adjustedPrice);
        }
      }

      setVariantId(selectedVariant?.id || null);
      setVariantPricing(adjustedPricing || null);
      setVariantStock(selectedVariant?.quantityAvailable || 0);
    };

    updateVariant();
  }, [attributesFormData, product.variants, product.productType]);

  return { variantId, variantStock, variantPricing };
};